import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';

import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Clickable, Sub, Table, TabButton, Tabs,
} from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import {
  Q, fetchUnits, formatNum, metaOf, metricByCode, metricText, orgComparisonLink, reasonText, resultDiff, unitLabel,
} from '../../performance-results/config';

// PM analitik P2 — E01 alt alan: organizasyon özeti + KRİTİK BİRİM. Birim satırları birim karşılaştırma ucundan (E10 ile aynı
// hesap ve gizleme); kritik birim açık eşikli kuraldır (şirkete göre tamamlanma, hedef, düşük performans ve ortalama puan açığı)
// ve nedenleriyle gösterilir — "kalite puanı" değildir. Satır → E10 alt birim (aynı süzgeçlerle, birim çekmecesi açık).
export const TOP_UNITS = 8;
export const SUMMARY_GROUPINGS = ['department', 'hierarchy'];
const COLUMNS = ['M01', 'M02', 'M06', 'M09', 'M07', 'M08'];

const Callout = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid ${({ theme }) => theme.colors.auxiliar.danger};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const CalloutIcon = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  display: inline-flex;
  padding-top: 2px;
`;

const Strong = styled.div`
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const Delta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme, $tone }) => ($tone === 'good' ? theme.colors.secondary.dark : $tone === 'bad' ? theme.colors.auxiliar.danger : theme.colors.text.muted)};
`;

const Flag = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin-left: 4px;
  display: inline-flex;
  vertical-align: middle;
`;

export default function OrgSummaryPanel({ filters, comparePeriod, onMeta, span = 8 }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [groupBy, setGroupBy] = useState('department');
  const key = JSON.stringify(filters);
  const panel = usePanel(
    () => fetchUnits(filters, { groupBy, comparePeriodId: comparePeriod?.id }),
    [key, groupBy, comparePeriod?.id],
    !!filters.periodId,
  );
  useEffect(() => { if (panel.data) onMeta?.('orgSummary', metaOf(panel.data)); }, [panel.data, onMeta]);

  const data = panel.data;
  const units = (data?.units || []).slice(0, TOP_UNITS);
  const critical = data?.criticalUnit || null;
  const target = (unit) => orgComparisonLink(filters, {
    [Q.groupBy]: groupBy,
    [Q.focus]: unit.key,
    ...(groupBy === 'hierarchy' && data?.parentKey ? { [Q.parent]: data.parentKey } : {}),
  });
  const all = orgComparisonLink(filters, { [Q.groupBy]: groupBy });

  return (
    <PanelCard
      panelKey="orgSummary" title={t('performance.executive.org.title')} span={span}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      empty={!!data && units.length === 0} emptyText={t('performance.executive.org.empty')}
      actions={<Link to={all} data-link="org-all">{t('performance.executive.org.seeAll')}</Link>}
    >
      <Tabs role="tablist">
        {SUMMARY_GROUPINGS.map((g) => (
          <TabButton key={g} role="tab" aria-selected={groupBy === g} $active={groupBy === g} onClick={() => setGroupBy(g)} data-tab={g}>
            {t(`performance.results.analytics.groupBy.${g}`)}
          </TabButton>
        ))}
      </Tabs>
      {critical ? (
        <Callout data-critical={critical.key}>
          <CalloutIcon><AlertTriangle size={16} aria-hidden="true" /></CalloutIcon>
          <div>
            <Strong>{t('performance.executive.org.criticalUnit', { name: unitLabel(t, critical) })}</Strong>
            <div>{critical.criticalReasons.map((r) => reasonText(t, r, lang)).join(' · ')}</div>
            <Link to={target(critical)} data-link="critical">{t('performance.executive.org.inspect')}</Link>
          </div>
        </Callout>
      ) : (
        data ? <Sub data-critical="none">{t('performance.executive.org.noCritical')}</Sub> : null
      )}
      <Table data-view="table">
        <colgroup><col style={{ width: '22%' }} />{COLUMNS.map((c) => <col key={c} />)}<col /></colgroup>
        <thead>
          <tr>
            <th>{t('performance.results.analytics.unit.col.unit')}</th>
            {COLUMNS.map((c) => <th key={c}>{t(`performance.results.analytics.metric.${c}`)}</th>)}
            <th>{t('performance.results.analytics.unit.col.compare')}</th>
          </tr>
        </thead>
        <tbody>
          {data ? (
            <tr data-row="header">
              <td><Strong>{data.header.kind === 'company' ? t('performance.results.analytics.unit.company') : unitLabel(t, data.header)}</Strong></td>
              {COLUMNS.map((c) => <td key={c}>{metricText(t, metricByCode(data.header.metrics, c), lang)}</td>)}
              <td>-</td>
            </tr>
          ) : null}
          {units.map((u) => {
            const diff = u.compare ? resultDiff('M06', metricByCode(u.metrics, 'M06'), metricByCode(u.compare, 'M06')) : null;
            return (
              <tr key={u.key} data-row={u.key}>
                <td title={unitLabel(t, u)}>
                  <Clickable type="button" onClick={() => history.push(target(u))} data-action="open-unit">{unitLabel(t, u)}</Clickable>
                  {u.critical ? <Flag title={t('performance.executive.org.criticalFlag')}><AlertTriangle size={12} aria-label={t('performance.executive.org.criticalFlag')} /></Flag> : null}
                </td>
                {COLUMNS.map((c) => <td key={c} data-cell={`${u.key}-${c}`}>{metricText(t, metricByCode(u.metrics, c), lang)}</td>)}
                <td>
                  {diff && diff.delta !== undefined ? (
                    <Delta $tone={diff.delta > 0 ? 'good' : diff.delta < 0 ? 'bad' : 'neutral'}>
                      {diff.delta > 0 ? <ArrowUp size={12} aria-hidden="true" /> : diff.delta < 0 ? <ArrowDown size={12} aria-hidden="true" /> : null}
                      {`${diff.delta > 0 ? '+' : ''}${formatNum(diff.delta, lang)}`}
                    </Delta>
                  ) : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {data && data.units.length > TOP_UNITS ? (
        <Sub data-note="more">{t('performance.executive.org.more', { count: data.units.length - TOP_UNITS })}</Sub>
      ) : null}
    </PanelCard>
  );
}
